import { SeparatorProps } from '@chakra-ui/react';
import { formatInteger, pluralize } from '@monetize/utils/core';
import { ReactNode } from 'react';
import { FaChevronRight } from 'react-icons/fa';
import {
  MCustomIconButton,
  MDivider,
  MFlex,
  MGrid,
  MText,
} from '../../../../../components/Monetize';
import { AccountLookupPopover } from '../../../../../components/Monetize/LookupPopover';

/**
 * Small muted field label paired inline with an account (e.g. "Ship To"). The
 * account name opens the standard account preview popover, so a group's
 * shipping/billing target can be checked without leaving the quote.
 */
export const GroupHeaderAccountField = ({
  label,
  accountId,
  accountName,
}: {
  label: string;
  accountId: string;
  accountName: string;
}) => (
  <MFlex alignItems="baseline" gap={1.5} minW={0}>
    <MText
      fontSize="xs"
      fontWeight="semibold"
      color="fg.muted"
      textTransform="uppercase"
      letterSpacing="0.02em"
      whiteSpace="nowrap"
      // The label is what makes the account name mean something, so it holds
      // its width and the name absorbs the squeeze
      flexShrink={0}
    >
      {label}
    </MText>
    {accountId ? (
      <AccountLookupPopover
        value={accountId}
        // The header strip is a single row, so the trigger stays on one line
        // rather than the popover's default label-over-id block.
        buttonProps={{ lineClamp: 1, minW: 0 }}
      >
        {accountName}
      </AccountLookupPopover>
    ) : (
      <MText lineClamp={1}>{accountName}</MText>
    )}
  </MFlex>
);

export const GroupHeaderDivider = (props: SeparatorProps) => (
  <MDivider
    orientation="vertical"
    h={5}
    borderColor="border.subtle"
    flexShrink={0}
    {...props}
  />
);

/**
 * Accessible name for a container's collapse control. Qualified by the table
 * section when there is more than one, because a group holding both existing
 * and newly added offerings renders a container in each.
 */
export const getGroupToggleLabel = (title: string, sectionLabel?: string) =>
  sectionLabel ? `Toggle ${title} in ${sectionLabel}` : `Toggle ${title}`;

interface QuoteOfferingGroupHeaderShellProps {
  /** Leading label, e.g. "Group 2" or "Ungrouped" */
  title: string;
  offeringCount: number;
  /** Pre-formatted sum of the run's offerings, ramp segments included */
  subtotal: string;
  isOpen: boolean;
  onToggle: () => void;
  /**
   * Names the collapse control. Purpose-based and stable across toggles — the
   * chevron's rotation carries the open/closed state.
   */
  toggleLabel: string;
  testId: string;
  /** Identifying fields rendered between the title and the offering count */
  fields?: ReactNode;
  /**
   * Rendered at the end of the left-hand run, after the offering count. For
   * things that open onto more detail rather than identify the run — supply
   * any separator with it, since this slot is often empty.
   */
  trailing?: ReactNode;
  /** Trailing action, e.g. delete. Its slot is reserved either way so the
   * subtotal sits on the same x-axis whether or not an action renders. */
  action?: ReactNode;
}

/**
 * Header strip shared by the grouped and ungrouped offering containers.
 *
 * Three areas — title, details, total. Wide enough and they sit on one line;
 * below `xl` the details drop to a second row, keeping the title and the total
 * (the two things you scan a stack of containers for) pinned to the top line.
 *
 * The details also wrap within their own area, which is the backstop: viewport
 * breakpoints can't see how wide this container actually is, so on a narrow
 * table inside a wide window the run re-flows rather than colliding.
 */
export const QuoteOfferingGroupHeaderShell = ({
  title,
  offeringCount,
  subtotal,
  isOpen,
  onToggle,
  toggleLabel,
  testId,
  fields,
  trailing,
  action,
}: QuoteOfferingGroupHeaderShellProps) => (
  <MGrid
    alignItems="center"
    columnGap={3}
    rowGap={1}
    templateColumns={{
      base: 'minmax(0, 1fr) auto',
      xl: 'auto minmax(0, 1fr) auto',
    }}
    templateAreas={{
      base: `"title total" "details details"`,
      xl: `"title details total"`,
    }}
    px={1}
    py={2}
    bg="bg.subtle"
    borderBottomWidth="1px"
    borderBottomStyle="solid"
    borderBottomColor="border.subtle"
    data-testid={testId}
  >
    <MFlex gridArea="title" alignItems="center" gap={3} minW={0}>
      <MCustomIconButton
        btnSize={2.5}
        containerSize={6}
        icon={FaChevronRight}
        iconProps={{
          transform: isOpen ? 'rotate(90deg)' : undefined,
          transition: 'all ease 0.3s',
        }}
        aria-label={toggleLabel}
        aria-expanded={isOpen}
        onClick={onToggle}
        data-testid={`${testId}-toggle`}
      />
      <MText fontWeight="semibold" whiteSpace="nowrap">
        {title}
      </MText>
    </MFlex>

    <MFlex
      gridArea="details"
      alignItems="center"
      flexWrap="wrap"
      columnGap={3}
      rowGap={1}
      minW={0}
      // Stacked, the run starts under the title rather than under the chevron —
      // 9 is the toggle's 6 plus the title gap of 3
      pl={{ base: 9, xl: 0 }}
    >
      {/* Separates the details from the title. On the stacked layout the row
          break already does that, so it would only dangle at the line start. */}
      <GroupHeaderDivider display={{ base: 'none', xl: 'block' }} />
      {fields}
      {!!fields && <GroupHeaderDivider />}
      <MText color="fg.muted" whiteSpace="nowrap" flexShrink={0}>
        {formatInteger(offeringCount)} {pluralize('offering', offeringCount)}
      </MText>
      {trailing}
    </MFlex>

    <MFlex gridArea="total" alignItems="center" gap={1} justifySelf="end">
      <MText
        fontWeight="bold"
        whiteSpace="nowrap"
        data-testid={`${testId}-subtotal`}
      >
        {subtotal}
      </MText>
      <MFlex minW={6} justifyContent="flex-end">
        {action}
      </MFlex>
    </MFlex>
  </MGrid>
);
